/**
 * The quality tier this tab actually runs at, and who to tell when it moves.
 *
 * Three answers, ranked. `?tier=` outranks everything, because the screenshot tool uses it to shoot
 * the same frame at every tier and a saved preference silently winning would make those frames lie.
 * Then the player's own knob, because the machine's guess is a guess and the player is the one
 * watching the frame rate. Then `defaultTier`, the guess itself, for everyone who never touched the
 * knob.
 *
 * Announced rather than polled, on the terms `motionPreference` states: anything built to a tier
 * rather than asking per frame subscribes and rebuilds. A mode launched after the change reads the
 * new answer at creation and needs no subscription at all.
 */

import { defaultTier, forcedTier } from '@/render/post/qualityTier';
import type { QualityTier } from '@/render/post/qualityTier';

let chosen: QualityTier | null = null;
const listeners = new Set<() => void>();

/** The tier to run at, resolved through the ranking above. */
export function preferredTier(): QualityTier {
  return forcedTier() ?? chosen ?? defaultTier();
}

/** Null is the player handing the choice back: the machine's own guess resumes. */
export function setPreferredTier(tier: QualityTier | null): void {
  if (tier === chosen) return;
  chosen = tier;
  for (const listener of listeners) listener();
}

/** Subscribes to the knob moving. Returns an unsubscribe, which a pipeline calls on dispose. */
export function onTierPreferenceChange(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}
